/**
 * Connection Validation Service
 *
 * Validates session connections with progress reporting.
 * Used when resuming a session to test each connection before loading.
 */

import type {
  Session,
  SessionConnection,
  SessionConnectionStatus,
  FailedConnectionDecision,
} from '../types/sessionTypes';
import type { Connection } from '../../../types/electron';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { connectionNeedsPasswordPrompt } from '../../connections/utils/connectPasswordPredicate';
import { resolveEagerConnectionIds } from './lazyConnect';
import { tabConnectionIds } from '../utils/tabConnectionIds';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

// ============================================================================
// Types
// ============================================================================

/**
 * Result of validating a single connection
 */
export type ValidationResult = {
  connectionId: string;
  success: boolean;
  error?: string;
  /**
   * Validated but deliberately NOT opened by this pass. `success: true` because
   * nothing is wrong with it (no decision UI, no failure), but it is not
   * connected: consumers that map a result onto a connection *status* must read
   * this flag.
   */
  deferred?: boolean;
  /**
   * Who opens a deferred connection, and when. `'background'` — the restore's
   * post-commit sweep, moments later, with no user action. `'password'` — only
   * the first tab that needs it, because opening it raises the single-instance
   * on-connect password prompt and the sweep therefore skips it. The dialog
   * says which, so a connection that really will sit closed isn't reported the
   * same as one that is already being opened.
   */
  deferredReason?: 'background' | 'password';
};

/**
 * Progress information during validation
 */
export type ValidationProgress = {
  /** Total number of connections to validate */
  total: number;
  /** Number of connections validated so far */
  completed: number;
  /** Current connection being validated */
  current: {
    connectionId: string;
    connectionName: string;
  } | null;
  /** Results collected so far */
  results: ValidationResult[];
  /**
   * True once every connection the restored workspace LANDS ON has settled —
   * connected, failed, or deliberately not attempted.
   *
   * This is the moment the workspace becomes usable, and therefore the moment
   * the dialog may be dismissed: the remaining connections are still being
   * opened, but nothing the user is about to look at is waiting on them. It is
   * reported rather than inferred because only this pass knows which ids were
   * in the eager set and which of them are still outstanding.
   */
  tabsReady: boolean;
};

/**
 * Callback for progress updates
 */
export type ProgressCallback = (progress: ValidationProgress) => void;

// ============================================================================
// Service Functions
// ============================================================================

/**
 * Validates all connections from a session.
 * Reports progress via callback and returns results.
 *
 * Two different checks, deliberately scoped differently since connect-on-resume
 * split into an eager set and a background sweep (E8):
 *
 * - **config existence** — for EVERY connection. A session that references a
 *   deleted connection must still reach the missing/removed decision UI, and
 *   that check costs nothing.
 * - **reachability** — for every connection, in a deliberate ORDER:
 *   `resolveEagerConnectionIds` first (everything the active tab needs — both
 *   endpoints of a Schema/Data Compare tab, so a failure on either side still
 *   reaches the decision UI), then the rest.
 *
 *   The order is what keeps a resume unblocked. It used to be scope: the rest
 *   weren't dialed here at all, they were seeded closed and swept after the
 *   restore committed. That kept the workspace fast but moved visible work out
 *   of the one surface built to show it — connections opened with no progress
 *   anywhere, and the Object Explorer had no state for "being opened", so the
 *   whole dial window rendered as the amber failure treatment. Now they are
 *   dialed here, `tabsReady` reports when the eager set has settled, and the
 *   dialog offers "Continue in background" from that moment: the workspace
 *   comes back exactly as early as before, and whatever is still dialing says
 *   so in the sidebar instead of looking broken.
 *
 *   One population still isn't dialed here: non-eager connections that need
 *   the single-instance password prompt. See the guard below.
 *
 * @param session - The session containing connections to validate
 * @param savedConnections - All saved connection configurations
 * @param onProgress - Callback for progress updates
 * @param signal - Aborts the pass when the user cancels. Without it, closing
 *   the dialog only stopped the UI from *listening*: this loop kept walking the
 *   remaining connections and opening every one of them.
 * @returns Array of validation results for each connection
 */
export async function validateSessionConnections(
  session: Session,
  savedConnections: Connection[],
  onProgress: ProgressCallback,
  signal?: AbortSignal
): Promise<ValidationResult[]> {
  const connectionStore = useConnectionStore.getState();
  // Get unique connection IDs from session
  const uniqueConnectionIds = [...new Set(
    session.connections.map(c => c.connectionId)
  )];
  // `savedConnections` is what makes the three callers agree on the eager set:
  // a candidate whose config is gone, or that needs an Entra auth window, is
  // skipped here exactly as the restore and the featured card's chips skip it.
  const eagerConnectionIds = new Set(resolveEagerConnectionIds(session, savedConnections));

  // The eager set goes FIRST. It is what the restored workspace lands on, so
  // settling it is what lets the dialog be dismissed — everything after it is
  // work the user can watch from the Object Explorer instead.
  const orderedConnectionIds = [
    ...uniqueConnectionIds.filter((id) => eagerConnectionIds.has(id)),
    ...uniqueConnectionIds.filter((id) => !eagerConnectionIds.has(id)),
  ];
  /** Eager ids not yet settled — `tabsReady` is this going empty. */
  const eagerOutstanding = new Set(
    orderedConnectionIds.filter((id) => eagerConnectionIds.has(id))
  );

  const results: ValidationResult[] = [];

  const report = (current: ValidationProgress['current']) =>
    onProgress({
      total: orderedConnectionIds.length,
      completed: results.length,
      current,
      results: [...results],
      tabsReady: eagerOutstanding.size === 0,
    });

  // Report initial progress
  report(null);

  // A connect that is already dialing cannot be aborted, but the intent behind
  // it can be withdrawn — `abandonConnect` makes the store close it on arrival
  // instead of adopting it. One listener for the whole pass; `dialingId` is
  // whichever connection the loop is currently waiting on.
  let dialingId: string | null = null;
  const abandonDialing = () => {
    if (dialingId) useConnectionStore.getState().abandonConnect(dialingId);
  };
  signal?.addEventListener('abort', abandonDialing, { once: true });

  for (let i = 0; i < orderedConnectionIds.length; i++) {
    if (signal?.aborted) break;
    const connectionId = orderedConnectionIds[i];
    const connection = savedConnections.find(c => c.id === connectionId);
    /**
     * Settle this id, so `tabsReady` can flip the moment the last one lands —
     * and PUBLISH it, because the flip is worthless to the dialog until it is
     * reported.
     *
     * Deleting alone left the news sitting in this closure until the NEXT
     * iteration's `report` at the top of the loop, so "Continue in background"
     * unlocked one connection late. For the single-connection session it never
     * unlocked at all: there is no next iteration, so the only report left is
     * the final one — which lands in the same tick the pass completes and the
     * dialog leaves the `validating` phase that renders the button. The button
     * exists to let the user in the moment their active tab's connection is
     * up; publishing here is what makes that its actual behaviour.
     */
    const settle = () => {
      eagerOutstanding.delete(connectionId);
      report(null);
    };

    // Report current connection being validated
    report(
      connection
        ? { connectionId, connectionName: safeConnectionName(connection) }
        : { connectionId, connectionName: 'Unknown connection' }
    );

    if (!connection) {
      // Connection config was deleted
      results.push({
        connectionId,
        success: false,
        error: 'Connection configuration not found',
      });
      settle();
      continue;
    }

    if (!eagerConnectionIds.has(connectionId) && connectionNeedsPasswordPrompt(connection)) {
      // The one population this pass still refuses to open. `connectPassword-
      // PromptStore` holds a single pending request, so raising its prompt here
      // would cancel — or be cancelled by — any other. It keeps the original
      // contract: it opens the first time a tab needs it, with the click as the
      // intent, and the row says "Needs password" rather than promising a
      // connect that isn't coming.
      results.push({
        connectionId,
        success: true,
        deferred: true,
        deferredReason: 'password',
      });
      continue;
    }

    try {
      // Session-restore validation pass — never a first-connect bridge trigger.
      dialingId = connectionId;
      const success = await connectionStore.connectToDatabase(connection, { initiator: 'restore' });
      dialingId = null;
      if (signal?.aborted) {
        // Cancelled while this one was dialing. The store has already closed it
        // on arrival; recording a result would only feed a UI nobody is looking
        // at any more.
        break;
      }
      if (success) {
        results.push({ connectionId, success: true });
      } else {
        const storeError = useConnectionStore.getState().connectionErrorById[connectionId];
        results.push({
          connectionId,
          success: false,
          error: storeError || 'Failed to connect',
        });
        // Close the failed connection to clean up any partial state
        try {
          await connectionStore.disconnectFromDatabase(connectionId);
        } catch {
          // Ignore cleanup errors
        }
      }
      settle();
    } catch (error) {
      dialingId = null;
      results.push({
        connectionId,
        success: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      });
      settle();
    }
  }

  signal?.removeEventListener('abort', abandonDialing);

  // Report final progress
  report(null);

  return results;
}

/**
 * Updates session connection statuses based on validation results.
 *
 * @param session - The session to update
 * @param results - Validation results
 * @returns Updated session with connection statuses
 */
export function applyValidationResults(
  session: Session,
  results: ValidationResult[]
): Session {
  const resultMap = new Map(results.map(r => [r.connectionId, r]));

  const updatedConnections: SessionConnection[] = session.connections.map(conn => {
    const result = resultMap.get(conn.connectionId);
    if (!result) {
      return conn;
    }

    // Deferred means "validated, not opened" — only an actual connect earns
    // 'connected'.
    const status: SessionConnectionStatus =
      result.success && !result.deferred ? 'connected' : 'disconnected';
    return {
      ...conn,
      status,
      error: result.error,
    };
  });

  return {
    ...session,
    connections: updatedConnections,
  };
}

/**
 * Gets failed connections from validation results.
 *
 * @param results - Validation results
 * @returns Array of failed validation results
 */
export function getFailedConnections(results: ValidationResult[]): ValidationResult[] {
  return results.filter(r => !r.success);
}

/**
 * Checks if all connections validated successfully.
 *
 * @param results - Validation results
 * @returns True if all connections succeeded
 */
export function allConnectionsSucceeded(results: ValidationResult[]): boolean {
  return results.every(r => r.success);
}

/**
 * Applies user decisions to failed connections in a session.
 *
 * - "keep" → marks connection as "disconnected" (tabs will load but not execute)
 * - "remove" → removes the connection and all its tabs from the session
 *
 * @param session - The session to modify
 * @param decisions - User decisions for failed connections
 * @returns Modified session with decisions applied
 */
export function applyConnectionDecisions(
  session: Session,
  decisions: FailedConnectionDecision[]
): Session {
  const decisionMap = new Map(decisions.map(d => [d.connectionId, d]));

  // Filter out connections that were removed
  const updatedConnections: SessionConnection[] = session.connections
    .filter(conn => {
      const decision = decisionMap.get(conn.connectionId);
      // Keep if no decision or decision is not "remove"
      return !decision || decision.decision !== 'remove';
    })
    .map(conn => {
      const decision = decisionMap.get(conn.connectionId);
      if (decision && decision.decision === 'keep') {
        // Mark as disconnected for "keep" decisions
        return {
          ...conn,
          status: 'disconnected' as SessionConnectionStatus,
          error: decision.error,
        };
      }
      return conn;
    });

  // Filter out tabs for removed connections
  const removedConnectionIds = new Set(
    decisions
      .filter(d => d.decision === 'remove')
      .map(d => d.connectionId)
  );

  // A tab goes with the connection it needs — and for compare tabs (two
  // endpoints in `content`) and blueprint tabs (the folder's active link) that
  // need is invisible in `connectionInfo`. Reading only `connectionInfo` kept
  // those tabs behind, contradicting what the resume card promises.
  const updatedTabs = session.tabs.filter(
    tab => !tabConnectionIds(tab).some(id => removedConnectionIds.has(id))
  );

  // Update active tab ID if it was removed
  const activeTabRemoved = session.activeTabId &&
    !updatedTabs.find(t => t.id === session.activeTabId);
  const newActiveTabId = activeTabRemoved
    ? (updatedTabs[0]?.id || null)
    : session.activeTabId;

  return {
    ...session,
    connections: updatedConnections,
    tabs: updatedTabs,
    activeTabId: newActiveTabId,
  };
}

/**
 * Checks if all failed connections have been decided.
 *
 * @param decisions - User decisions for failed connections
 * @returns True if all decisions are made (none are "pending")
 */
export function allDecisionsMade(decisions: FailedConnectionDecision[]): boolean {
  return decisions.every(d => d.decision !== 'pending');
}

/**
 * Creates initial decision objects for failed connections.
 *
 * @param results - Validation results
 * @param savedConnections - All saved connection configurations
 * @returns Array of pending decisions for failed connections
 */
export function createPendingDecisions(
  results: ValidationResult[],
  savedConnections: Connection[]
): FailedConnectionDecision[] {
  return results
    .filter(r => !r.success)
    .map(r => {
      const savedConn = savedConnections.find(c => c.id === r.connectionId);
      return {
        connectionId: r.connectionId,
        connectionName: safeConnectionName(savedConn, 'Unknown'),
        error: r.error || 'Connection failed',
        decision: 'pending' as const,
      };
    });
}

/**
 * Creates "remove" decisions for connections the user switched off on the
 * Sessions dialog's featured card before resuming.
 *
 * Excluding a connection is the same operation as dropping a failed one: the
 * connection and its tabs leave the session, so the resume neither connects it
 * nor keeps it. Feeding these through `applyConnectionDecisions` keeps a
 * single removal path rather than a parallel mechanism. `error` is empty
 * because nothing failed — the user simply doesn't want it back.
 *
 * @param connectionIds - Connection ids the user excluded
 * @param savedConnections - All saved connection configurations (for labels)
 */
export function createExclusionDecisions(
  connectionIds: string[],
  savedConnections: Connection[]
): FailedConnectionDecision[] {
  return connectionIds.map(connectionId => {
    const savedConn = savedConnections.find(c => c.id === connectionId);
    return {
      connectionId,
      connectionName: safeConnectionName(savedConn, 'Unknown'),
      error: '',
      decision: 'remove' as const,
    };
  });
}
